<script setup lang="ts">
import { $t } from '@teachino/shared-i18n';
import { computed, ref } from 'vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const MAX_OCR_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    extractText: (imageFile: File) => Promise<string>;
    extractionErrorLabel?: string;
    fileTooLargeLabel?: string;
    maxFileSizeBytes?: number;
  }>(),
  {
    disabled: false,
    extractionErrorLabel: $t('image_to_text.extraction_error'),
    fileTooLargeLabel: $t('image_to_text.file_too_large'),
    maxFileSizeBytes: MAX_OCR_FILE_SIZE_BYTES,
  },
);

const emit = defineEmits<{
  error: [error: unknown];
  extracted: [text: string];
}>();

const isProcessing = ref(false);
const extractionError = ref('');
const fileInputRef = ref<HTMLInputElement | null>(null);

const isBusy = computed(() => props.disabled || isProcessing.value);

function openFilePicker() {
  if (isBusy.value) {
    return;
  }
  extractionError.value = '';
  fileInputRef.value?.click();
}

async function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) {
    return;
  }

  // Reset input so the same file can be selected again
  input.value = '';

  if (file.size > props.maxFileSizeBytes) {
    extractionError.value = props.fileTooLargeLabel;
    return;
  }

  isProcessing.value = true;
  extractionError.value = '';

  try {
    const text = await props.extractText(file);
    if (text) {
      emit('extracted', text);
    }
  } catch (error: unknown) {
    console.error(error);
    emit('error', error);
    extractionError.value = props.extractionErrorLabel;
  } finally {
    isProcessing.value = false;
  }
}
</script>

<template>
  <div class="relative inline-flex flex-col items-end">
    <button
      type="button"
      class="inline-flex items-center justify-center rounded-full border border-transparent p-2 text-slate-700 transition-transform hover:border-slate-200 hover:bg-white disabled:cursor-not-allowed disabled:text-neutral-400"
      :disabled="isBusy"
      :title="$t('image_to_text.upload_image')"
      @click="openFilePicker"
    >
      <HugeIcon v-if="isProcessing" icon="loading" :spin="true" :size="20" />
      <HugeIcon v-else icon="camera" :size="20" />
    </button>

    <input
      ref="fileInputRef"
      type="file"
      accept="image/*"
      capture="environment"
      class="hidden"
      @change="handleFileChange"
    />

    <div
      v-if="extractionError"
      class="text-danger-500 absolute top-full right-0 z-10 mt-2 w-64 text-sm"
    >
      {{ extractionError }}
    </div>
  </div>
</template>
